import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getCsrfToken } from "@/lib/csrf";

export interface ReconcileCopy {
	unchanged: string;
	updated: (status: string) => string;
	failed: string;
}

/**
 * Asks the backend to re-derive one order's Bonus RO award and returns the toast to show.
 * The backend is idempotent, so a repeat answers awardChanged: false. Its error message
 * is shown as is; only a missing one falls back to `copy.failed`.
 */
export async function reconcileOrderBonus(
	orderId: string,
	copy: ReconcileCopy,
): Promise<{ ok: boolean; message: string }> {
	try {
		const res = await fetch(
			`/admin-portal/appointments/orders/${orderId}/reconcile-bonus`,
			{
				method: "POST",
				headers: {
					Accept: "application/json",
					"X-CSRF-Token": getCsrfToken(),
				},
			},
		);
		const json = await res.json().catch(() => null);
		if (!res.ok || !json?.success)
			return { ok: false, message: json?.error || copy.failed };

		const { awardChanged, status } = json.data ?? {};
		return {
			ok: true,
			message: awardChanged ? copy.updated(status ?? "") : copy.unchanged,
		};
	} catch {
		return { ok: false, message: copy.failed };
	}
}

export function useReconcileBonus(orderId?: string | null) {
	const { t } = useTranslation("appointments");
	const [pending, setPending] = useState(false);

	const reconcile = async () => {
		if (!orderId) return;
		setPending(true);
		const id = toast.loading(t("list.reconcile_bonus.pending"));
		const result = await reconcileOrderBonus(orderId, {
			unchanged: t("list.reconcile_bonus.unchanged"),
			updated: (status) => t("list.reconcile_bonus.updated", { status }),
			failed: t("list.reconcile_bonus.failed"),
		});
		setPending(false);
		if (result.ok) toast.success(result.message, { id });
		else toast.error(result.message, { id });
	};

	return {
		pending,
		disabled: pending || !orderId,
		reconcile,
		label: t("list.reconcile_bonus.button"),
	};
}
